import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getAccountList,
  getContactList,
  getGenericMasterFilteredListByKey,
  getGridDataByModuleKey,
  getLeadList,
  getOpportunityList,
} from '../../redux/selector';
import { fetchCampaignsAndAppendToModule } from '../../redux/actions';
import { useDispatch, useSelector } from 'react-redux';
import apiServices from '../../services/service';
import { DateFormatter } from '../../shared/controls/formatters';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { campaignInputData, handleGridSearch } from '../../shared/constants';
import CustomPagination from './grid/customPagination';

/* GrowthOS icons + helpers for the Campaigns tab (design growthos/Campaigns) */
const CMP_ICONS = {
  campaigns: <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1ZM15 8a4 4 0 0 1 0 8M18 5a8 8 0 0 1 0 14" />,
  mail: <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7" />,
  note: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" />,
  target: (<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></>),
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  refresh: <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />,
  zap: <path d="M13 2L3 14h7l-1 8 10-12h-7z" />,
  calendar: (<><rect x="3" y="4.5" width="18" height="16.5" rx="2.5" /><path d="M16 2.5v4M8 2.5v4M3 9.5h18" /></>),
  spark: <path d="M12 3l1.9 5.2L19 10l-5.1 1.8L12 17l-1.9-5.2L5 10l5.1-1.8L12 3Z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  search: (<><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></>),
  x: <path d="M18 6 6 18M6 6l12 12" />,
  chevR: <path d="M9 18l6-6-6-6" />,
};
const CIc = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {CMP_ICONS[name] || null}
  </svg>
);
const TYPE_ICON = {
  'Outbound sequence': 'mail', 'Email nurture': 'mail', Email: 'mail', 'Content nurture': 'note',
  Retargeting: 'target', 'Account-based': 'building', Lifecycle: 'refresh', Broadcast: 'campaigns',
  'Multi-step': 'zap', Event: 'calendar', 'Re-engagement': 'spark',
};
const typeIcon = (t) => TYPE_ICON[t] || 'campaigns';
const CMP_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const cmpColor = (s) =>
  CMP_COLORS[String(s || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % CMP_COLORS.length];
const cmpInitials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
const statusTone = (s) => {
  const v = String(s || '').toLowerCase();
  if (v.includes('active') || v.includes('running')) return 'b-green';
  if (v.includes('complete')) return 'b-gray';
  if (v.includes('paus')) return 'b-amber';
  if (v.includes('schedul') || v.includes('plan')) return 'b-blue';
  if (v.includes('stop') || v.includes('cancel') || v.includes('fail')) return 'b-red';
  return 'b-gray';
};
const memberTone = (s) => {
  const v = String(s || '').toLowerCase();
  if (v.includes('convert') || v.includes('active') || v.includes('respond')) return 'b-green';
  if (v.includes('enroll') || v.includes('click') || v.includes('sent') || v.includes('plan')) return 'b-blue';
  if (v.includes('unsub') || v.includes('bounce') || v.includes('opt')) return 'b-red';
  return 'b-gray';
};

const ChildCampaign = ({ module, localData, hasUpdateCampaignPermission }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPopup2, setShowPopup2] = useState(false);
  const [showPopup3, setShowPopup3] = useState(false);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [query, setQuery] = useState('');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [filteredCampaignList, setFilteredCampaignList] = useState([]);
  const [editData, setEditData] = useState([]);
  const [deleteData, setDeleteData] = useState([]);
  const gridRef = useRef();
  const localGridRef = useRef();
  const gridData = useSelector(getGridDataByModuleKey('campaignList'));
  const [searchText, setSearchText] = useState(gridData?.inputPayload?.searchText || '');
  const [isFocused, setIsFocused] = useState(false);
  const campaignTotalCount = gridData?.totalCount;

  const [localCampaignInputPayload, setLocalCampaignInputPayload] = useState({
    ...campaignInputData,
    module: module,
    moduleId: localData?.id,
  });

  const [totalCount, setTotalCount] = useState(0);
  const gridApiRef = useRef(null);

  let hasInfoCampaignPermission = true;
  let userUIControlPermission = JSON.parse(
    localStorage.getItem('userUIControlPermission')
  )?.campaignmenu;

  if (userUIControlPermission) {
    hasInfoCampaignPermission = userUIControlPermission?.campaigninfoscreen
      ? userUIControlPermission.campaigninfoscreen.canView
      : true;
  }

  const onGridReady = (params) => {
    gridApiRef.current = params.api;
  };
  const onSelectionChanged = (event) => {
    setSelectedRows(event.api.getSelectedRows()); // Get all selected rows
  };

  const leadList = useSelector(getLeadList);
  const contactList = useSelector(getContactList);
  const accountList = useSelector(getAccountList);
  const opportunityList = useSelector(getOpportunityList);

  useEffect(() => {
    fetchCampaign();
  }, [localCampaignInputPayload.pageNumber, localCampaignInputPayload.pageSize, localData?.id]);

  const [campaignListAll, setCampaignListAll] = useState(null);

  const fetchCampaign = async () => {
    if (!localData?.id) return;
    if (searchText) {
      localCampaignInputPayload.searchText = searchText;
    } else {
      localCampaignInputPayload.searchText = null;
    }
    gridApiRef.current?.showLoadingOverlay();
    setLoading(true);
    setSelectedRows([]);
    try {
      const res = await apiServices.getCampaignList(localCampaignInputPayload);
      const campaigns = res.data.campaign || [];

      setCampaignListAll(campaigns);
      setTotalCount(res.data.totalCount || 0);

      if (campaigns.length === 0) {
        gridApiRef.current?.showNoRowsOverlay();
      } else {
        gridApiRef.current?.hideOverlay();
      }
    } catch (error) {
      console.error('Error fetching campaigns', error);
      gridApiRef.current?.showNoRowsOverlay();
    } finally {
      setLoading(false);
      setSelectedRows([]);
    }
  };

  const clearSearch = async () => {
    setSearchText('');

    const payload = {
      ...localCampaignInputPayload,
      pageNumber: 1,
      searchText: '',
    };

    try {
      gridApiRef.current?.showLoadingOverlay(); // ✅ Show loading before fetch
      setLoading(true);
      const res = await apiServices.getCampaignList(payload);
      const campaigns = res.data.campaign || [];

      setCampaignListAll(campaigns);
      setTotalCount(res.data.totalCount || 0);

      if (campaigns.length === 0) {
        gridApiRef.current?.showNoRowsOverlay();
      } else {
        gridApiRef.current?.hideOverlay();
      }
    } catch (error) {
      console.error('Search Clear Error', error);
      gridApiRef.current?.showNoRowsOverlay();
    } finally {
      setLoading(false);
    }
  };

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: () => (
            <CustomPagination
              inputPayload={localCampaignInputPayload}
              setInputPayload={setLocalCampaignInputPayload}
              totalCount={totalCount}
            />
          ),
          align: 'center',
        },
        { statusPanel: 'agSelectedRowCountComponent' },
      ],
    };
  }, [localCampaignInputPayload, totalCount, localData?.id]);

  const memberStatus = useSelector(getGenericMasterFilteredListByKey('MemberStatusCrm'));

  const campaignColumnDefs = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      headerName: '',
      maxWidth: 50,
      suppressSizeToFit: true,
    },
    { headerName: 'Campaign Name', field: 'campaignName', width: 150 },
    { headerName: 'Status', field: 'status', width: 150 },
    { headerName: 'Source', field: 'campaignType', width: 150 },
    {
      headerName: 'Start Date',
      field: 'startDate',
      width: 150,
      valueFormatter: DateFormatter,
    },
    {
      headerName: 'End Date',
      field: 'endDate',
      width: 150,
      valueFormatter: DateFormatter,
    },
    { headerName: 'Owner', field: 'owner', width: 150 },
  ];

  const localCampaignColumnDefs = [
    {
      headerName: 'Actions',
      cellRenderer: (params) => {
        const paramsValue = { ...params.data };
        return (
          <div className="action-menu">
            {hasUpdateCampaignPermission && (
              <>
                <img
                  src="/assets/images/edit.png"
                  className="action-img1"
                  onClick={() => handleClickEdit(paramsValue)}
                  alt="Edit Contact"
                  title="Edit Contact"
                ></img>
                <img
                  src="/assets/images/trash.png"
                  onClick={() => openPopup3(paramsValue)}
                  alt="Delete Contact"
                  title="Delete Contact"
                  width="12px"
                ></img>
              </>
            )}
          </div>
        );
      },
      maxWidth: 120,
      sortable: false,
      filter: false,
      pinned: 'left',
    },
    {
      headerName: 'Campaign Name',
      field: 'campaignName',
      cellRenderer: (params) => {
        if (!params.value) return null; // Handle empty or null values
        return hasInfoCampaignPermission ? (
          <Link
            to={`/campaigns/viewCampaign?id=${params.data.id}&pageNo=${gridData?.inputPayload?.pageNumber}`}
            state={params.data}
            style={{ fontWeight: '600' }}
          >
            {params.value}
          </Link>
        ) : (
          <span style={{ fontWeight: '600' }}>{params.value}</span>
        );
      },

      width: 150,
      filter: 'agSetColumnFilter',
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 150,
      filter: 'agSetColumnFilter',
    },
    {
      headerName: 'Type',
      field: 'campaignType',
      width: 150,
      filter: 'agSetColumnFilter',
    },
    {
      headerName: 'Start Date',
      field: 'startDate',
      width: 150,
      valueFormatter: DateFormatter,
      filter: 'agSetColumnFilter',
    },
    {
      headerName: 'End Date',
      field: 'endDate',
      width: 150,
      valueFormatter: DateFormatter,
      filter: 'agSetColumnFilter',
    },
    {
      headerName: 'Member Status',
      field: 'memberStatus',
      width: 150,
      filter: 'agSetColumnFilter',
    },
    {
      headerName: 'Owner',
      field: 'owner',
      width: 150,
      filter: 'agSetColumnFilter',
    },
  ];

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          minWidth: 225,
          width: 225,
          maxWidth: 225,
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
      position: 'left',
      defaultToolPanel: 'none', //show off filter tab bydefault.
    };
  }, []);

  const onSearch = (searchValue) => {
    handleGridSearch({
      searchValue,
      apiMethod: apiServices.getCampaignList,
      gridApiRef,
      inputPayload: localCampaignInputPayload,
      setData: setCampaignListAll,
      setTotalCount,
      setLoading,
    });
  };

  const onFilterTextBoxChangedLocal = useCallback(() => {
    localGridRef.current.api.setQuickFilter(document.getElementById('filter-text-box').value);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedText = searchText.trim();
      if (trimmedText) {
        onSearch(trimmedText);
      } else {
        clearSearch(); // <--- this ensures it resets with empty searchText
      }
    }
  };

  const viewCampaign = (params) => {
    alert(`Viewing campaign: ${params.data.campaignName}`);
  };

  const editCampaign = (params) => {
    alert(`Editing campaign: ${params.data.campaignName}`);
  };

  const deleteCampaign = (params) => {
    alert(`Deleting campaign: ${params.data.campaignName}`);
  };

  const frameworkComponents = {
    checkboxRenderer: (props) => {
      const handleChange = (e) => {
        const isSelected = props.node.isSelected();
        props.node.setSelected(!isSelected); // Toggle the selection state of the row
      };

      return <input type="checkbox" checked={props.node.isSelected()} onChange={handleChange} />;
    },
  };

  const handleOpen = () => {
    setIsOpen(true);
    fetchCampaign();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const openPopup2 = () => {
    setShowPopup2(true);
  };

  const closePopup2 = () => {
    setShowPopup2(false);
  };
  // Function to close the third popup
  const closePopup3 = () => {
    setShowPopup3(false);
  };

  // Function to open the third popup
  const openPopup3 = (data) => {
    setShowPopup3(true);
    setDeleteData(data);
  };

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: selectedOption,
    }));

    handleLookupDropDownChange(selectedOption.value, selectedOption, name);
  };

  const handleLookupDropDownChange = (selectedId, selectedOption, name) => {
    const selectedValue = selectedOption.label;

    const updatedFormData = {
      ...formData,
      [name]: selectedId,
      [`${name}Name`]: selectedValue,
    };
    if (name === 'memberStatusId') {
      setFormData({
        ...formData,
        memberStatusId: selectedValue,
        memberStatus: selectedId,
      });
    }
  };

  useEffect(() => {
    const plannedOption = memberStatus?.find((option) => option.displayName === 'Planned');

    if (plannedOption) {
      setSelectedOptions((prevState) => ({
        ...prevState,
        memberStatusId: plannedOption.id,
      }));

      setFormData((prevState) => ({
        ...prevState,
        memberStatus: plannedOption.id,
        memberStatusId: plannedOption.displayName,
      }));
    }
  }, [memberStatus, showPopup2]);

  const handleSubmit = async (formData) => {
    const selectedIds = selectedRows.map((row) => row.id);

    // Define module-specific API services and dynamic key for leadId
    const moduleAPIs = {
      Lead: {
        add: apiServices.addCampaignFromLead,
        edit: apiServices.updateCampaignByModule,
        leadKey: 'leadId', // The key for Lead module
        leadValue: localData?.id, // The value for Lead module
      },
      Contact: {
        add: apiServices.addCampaignFromContact,
        edit: apiServices.updateCampaignByModule,
        leadKey: 'contactId', // The key for Contact module
        leadValue: localData?.id, // The value for Contact module
      },
      Account: {
        add: apiServices.addCampaignFromAccount,
        edit: apiServices.updateCampaignByModule,
        leadKey: 'accountId', // The key for Account module
        leadValue: localData?.id, // The value for Account module
      },
      Opportunity: {
        add: apiServices.addCampaignFromOpportunity,
        edit: apiServices.updateCampaignByModule,
        leadKey: 'opportunityId', // The key for Opportunity module
        leadValue: localData?.id, // The value for Opportunity module
      },
    };

    // Select the API based on the module and form type
    const selectedAPI =
      formData?.type === 'edit' ? moduleAPIs[module]?.edit : moduleAPIs[module]?.add;

    const leadKey = moduleAPIs[module]?.leadKey;
    const leadValue = moduleAPIs[module]?.leadValue;

    if (!selectedAPI || !leadKey || !leadValue) {
      toast.error('Invalid module or operation type.', {
        position: 'top-right',
      });
      return;
    }

    // Prepare the updated form data
    const updatedFormData = {
      ...formData,
      campaignId: formData?.type === 'edit' ? formData?.id : selectedIds,
      [leadKey]: [leadValue], // Dynamically set the correct leadId key based on the module
      module: module,
      moduleId: localData?.id,
    };

    setLoading(true);

    try {
      const response = await selectedAPI(updatedFormData);
      if (response.status === 200) {
        if (formData?.type !== 'edit') handleClose();
        else setShowPopup2(false);

        dispatch(
          fetchCampaignsAndAppendToModule({
            moduleName: module,
            id: localData?.id,
          })
        );

        toast.success(
          formData?.type === 'edit'
            ? 'Campaign updated successfully!'
            : 'Campaign added successfully!',
          { position: 'top-right' }
        );
      } else {
        const errorMessages = Object.values(response.errors || {}).flat();
        errorMessages.forEach((errorMessage) =>
          toast.error(errorMessage, { position: 'top-right' })
        );
      }
    } catch (error) {
      toast.error('An unexpected error occurred.', {
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  // Add a single campaign from the GrowthOS AddModal (design: click-to-add, default member status)
  const addCampaign = async (c) => {
    const moduleAPIs = {
      Lead: { add: apiServices.addCampaignFromLead, key: 'leadId' },
      Contact: { add: apiServices.addCampaignFromContact, key: 'contactId' },
      Account: { add: apiServices.addCampaignFromAccount, key: 'accountId' },
      Opportunity: { add: apiServices.addCampaignFromOpportunity, key: 'opportunityId' },
    };
    const cfg = moduleAPIs[module];
    if (!cfg || !localData?.id) {
      toast.error('Invalid module or record.', { position: 'top-right' });
      return;
    }
    const planned = memberStatus?.find((o) => o.displayName === 'Planned') || memberStatus?.[0];
    const payload = {
      campaignId: [c.id],
      [cfg.key]: [localData.id],
      module,
      moduleId: localData.id,
      memberStatus: planned?.id,
      memberStatusId: planned?.displayName,
    };
    setLoading(true);
    try {
      const res = await cfg.add(payload);
      if (res.status === 200) {
        dispatch(fetchCampaignsAndAppendToModule({ moduleName: module, id: localData.id }));
        toast.success('Campaign added successfully!', { position: 'top-right' });
        setIsOpen(false);
      } else {
        Object.values(res.errors || {}).flat().forEach((m) => toast.error(m, { position: 'top-right' }));
      }
    } catch (e) {
      toast.error('An unexpected error occurred.', { position: 'top-right' });
    } finally {
      setLoading(false);
    }
  };

  const handleClickDeleteCampaign = (paramsValue) => {
    setLoading(true);
    // Creating the desired JSON object
    const data = {
      campaignId: paramsValue.id,
      module: module,
      moduleId: [localData?.id],
    };
    console.log('Data==>>', data);
    apiServices
      .deleteCampaignByModule(data)
      .then((response) => {
        if (response.status === 200) {
          setShowPopup3(false);
          dispatch(
            fetchCampaignsAndAppendToModule({
              moduleName: module,
              id: localData?.id,
            })
          );
          setLoading(false);
          toast.success('Campaign Unassigned Successfully!', {
            position: 'top-right',
          });
        }
      })
      .catch((error) => {
        console.error('Error while deleting lead:', error);
        setLoading(false);
      });
  };

  const handleClickEdit = (data) => {
    setShowPopup2(true);
    setEditData({
      ...data,
      type: 'edit',
    });
  };

  const showPopupVisibility = () => {
    setShowPopup3(false); // Reset to default page on close
  };

  const campaigns = localData?.campaign || [];
  const q = query.trim().toLowerCase();
  const visible = q
    ? campaigns.filter((c) =>
        [c.campaignName, c.campaignType, c.memberStatus, c.status].some((f) =>
          String(f || '').toLowerCase().includes(q)
        )
      )
    : campaigns;
  const activeCount = campaigns.filter((c) => String(c.status || '').toLowerCase().includes('active')).length;
  const enrolledIds = campaigns.map((c) => c.id);
  const aq = (campaignSearch || '').trim().toLowerCase();
  const available = (campaignListAll || []).filter(
    (c) =>
      !enrolledIds.includes(c.id) &&
      (!aq || [c.campaignName, c.campaignType].some((f) => String(f || '').toLowerCase().includes(aq)))
  );

  return (
    <div className="gos-lr-card gos-cmp" id="campainview">
      <div className="gos-lr-card-head">
        <div className="gos-lr-card-title">
          <CIc name="campaigns" size={16} /> Campaigns
          {campaigns.length > 0 ? <span className="gos-lr-count">{campaigns.length}</span> : null}
        </div>
        {hasUpdateCampaignPermission ? (
          <button type="button" className="gos-cmp-add" onClick={handleOpen}>
            <CIc name="plus" size={14} /> Add to Campaign
          </button>
        ) : null}
      </div>

      {campaigns.length > 0 ? (
        <div className="gos-cmp-searchbar">
          <div className="gos-camp-search" style={{ margin: 0 }}>
            <CIc name="search" size={15} />
            <input
              placeholder="Search enrolled campaigns…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query ? (
              <button type="button" className="gos-camp-search-clear" onClick={() => setQuery('')}>
                <CIc name="x" size={12} />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {campaigns.length === 0 ? (
        <div className="gos-cmp-empty">
          <div className="gos-cmp-empty-ic">
            <CIc name="campaigns" size={21} />
          </div>
          <div className="gos-cmp-empty-title">Not enrolled in any campaigns</div>
          <div className="gos-cmp-empty-text">
            Add this {String(module || 'record').toLowerCase()} to a nurture sequence, outbound campaign, or broadcast list.
          </div>
          {hasUpdateCampaignPermission ? (
            <button type="button" className="gos-cmp-add" onClick={handleOpen}>
              <CIc name="plus" size={13} /> Add to Campaign
            </button>
          ) : null}
        </div>
      ) : visible.length === 0 ? (
        <div className="gos-cmp-nomatch">No campaigns match "{query}".</div>
      ) : (
        <div className="gos-cmp-list">
          {visible.map((c) => {
            const color = cmpColor(c.campaignType || c.campaignName);
            return (
              <div className="gos-cmp-row" key={c.id}>
                <span className="gos-cmp-ic" style={{ background: color + '22', color }}>
                  <CIc name={typeIcon(c.campaignType)} size={18} />
                </span>
                <div className="gos-cmp-main">
                  {hasInfoCampaignPermission ? (
                    <Link
                      to={`/campaigns/viewCampaign?id=${c.id}`}
                      state={c}
                      className="gos-cmp-name"
                    >
                      {c.campaignName}
                    </Link>
                  ) : (
                    <span className="gos-cmp-name">{c.campaignName}</span>
                  )}
                  <div className="gos-cmp-meta">
                    {c.campaignType ? <span className="gos-cmp-type">{c.campaignType}</span> : null}
                    {(c.startDate || c.endDate) ? (
                      <>
                        <span className="gos-cmp-dot">·</span>
                        <CIc name="calendar" size={12} />
                        <span>
                          {c.startDate ? DateFormatter({ value: c.startDate }) : '—'}
                          {' → '}
                          {c.endDate ? DateFormatter({ value: c.endDate }) : '—'}
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="gos-cmp-badges">
                  {c.status ? (
                    <span className={'gos-me-badge ' + statusTone(c.status)}>
                      <span className="gos-me-pip" />
                      {c.status}
                    </span>
                  ) : null}
                  {c.memberStatus ? (
                    <span className={'gos-me-badge ' + memberTone(c.memberStatus)}>{c.memberStatus}</span>
                  ) : null}
                </div>
                <div className="gos-cmp-owner">
                  {c.owner ? (
                    <>
                      <span className="gos-cmp-ava" style={{ background: cmpColor(c.owner) }}>
                        {cmpInitials(c.owner)}
                      </span>
                      <span className="gos-cmp-owner-name">{c.owner}</span>
                    </>
                  ) : null}
                </div>
                {hasUpdateCampaignPermission ? (
                  <button
                    type="button"
                    className="gos-lr-iconbtn gos-lr-iconbtn-sm gos-cmp-remove"
                    title="Remove from campaign"
                    onClick={() => openPopup3(c)}
                  >
                    <CIc name="x" size={15} />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {campaigns.length > 0 ? (
        <div className="gos-cmp-foot">
          <CIc name="campaigns" size={13} />
          <span>
            {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''} · {activeCount} active
          </span>
        </div>
      ) : null}

      {/* Add to Campaign modal (design) */}
      {isOpen ? (
        <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
          <div className="gos-cmp-addmodal" role="dialog" aria-modal="true" aria-label="Add to campaign">
            <div className="gos-cmp-addhead">
              <div className="gos-cmp-addtitle">
                <span className="gos-cmp-addtitle-ic"><CIc name="campaigns" size={15} /></span>
                Add to Campaign
              </div>
              <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm" onClick={handleClose} aria-label="Close">
                <CIc name="x" size={16} />
              </button>
            </div>
            <div className="gos-cmp-addbody">
              <div className="gos-camp-search">
                <CIc name="search" size={15} />
                <input
                  autoFocus
                  placeholder="Search campaigns…"
                  value={campaignSearch}
                  onChange={(e) => setCampaignSearch(e.target.value)}
                />
                {campaignSearch ? (
                  <button type="button" className="gos-camp-search-clear" onClick={() => setCampaignSearch('')}>
                    <CIc name="x" size={12} />
                  </button>
                ) : null}
              </div>
              <div className="gos-cmp-addlist">
                {loading && available.length === 0 ? (
                  <div className="gos-camp-empty">
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  </div>
                ) : available.length === 0 ? (
                  <div className="gos-camp-empty">No campaigns match.</div>
                ) : (
                  available.map((c) => {
                    const color = cmpColor(c.campaignType || c.campaignName);
                    return (
                      <button key={c.id} type="button" className="gos-owner-row" onClick={() => addCampaign(c)} disabled={loading}>
                        <span className="gos-camp-ic" style={{ background: color + '22', color }}>
                          <CIc name={typeIcon(c.campaignType)} size={16} />
                        </span>
                        <div className="gos-bulk-grow" style={{ minWidth: 0 }}>
                          <div className="gos-owner-name gos-camp-name">{c.campaignName}</div>
                          <div className="gos-owner-sub">{[c.campaignType, c.status].filter(Boolean).join(' · ')}</div>
                        </div>
                        <CIc name="chevR" size={14} style={{ color: 'var(--gos-text-3)', flex: 'none' }} />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Unassign confirmation (kept) */}
      {showPopup3 && (
        <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closePopup3(); }}>
          <div className="gos-cmp-confirm" role="dialog" aria-modal="true">
            <div className="gos-cmp-confirm-title">Remove from campaign?</div>
            <div className="gos-cmp-confirm-text">
              Unassign <strong>{deleteData.campaignName}</strong> from this {String(module || 'record').toLowerCase()}?
            </div>
            <div className="gos-cmp-confirm-actions">
              <button type="button" className="gos-btn gos-btn-ghost" onClick={closePopup3}>Cancel</button>
              <button type="button" className="gos-btn gos-btn-danger" onClick={() => handleClickDeleteCampaign(deleteData)} disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Removing…
                  </>
                ) : (
                  'Remove'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildCampaign;
